/**
 * RosterDataService - Centralized data management for Roster feature
 * 
 * This service handles all API calls and data fetching for the Roster component,
 * following the same pattern as ForecastVsActuals refactoring.
 * 
 * Key responsibilities:
 * - Fetch roster data from API or fallback to local data
 * - Fetch outcome allocations with caching
 * - Fetch unassigned roles with optimization
 * - Manage supporting data (outcomes, applications, tags, rates)
 * - Handle progressive data loading
 * - Cache management and cleanup
 */

import * as api from '../../../api/api';
import itRosterData from '../../../data/it_roster.json';
import { getLocationDisplayName } from '../utils/rosterCalculations';

// Utility function for safe string conversion
const safeString = (value, defaultValue = '') => value ? String(value) : defaultValue;

class RosterDataService {
  constructor() {
    this.cache = new Map();
    this.cacheTimestamps = new Map();
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    this.requestQueue = new Map();
  }

  /**
   * Fetch roster data with fallback to local data
   */
  async fetchRosterData(department = null) {
    const cacheKey = department ? `roster-data-${department}` : 'roster-data';

    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      console.log('📊 RosterDataService: Using cached roster data');
      return this.cache.get(cacheKey);
    }

    try {
      console.log('📊 RosterDataService: Fetching roster data from API...');

      let membersData;
      try {
        const membersResponse = await api.getITRoster(department);
        membersData = membersResponse?.data || membersResponse;
        console.log('✅ RosterDataService: Loaded roster from API:', membersData?.length || 0, 'members');
      } catch (error) {
        console.log('⚠️ RosterDataService: API failed, using local roster data:', error.message);
        membersData = itRosterData;
      }

      const processedMembers = (Array.isArray(membersData) ? membersData : [])
        .filter(member => member && member.id && member.name)
        .map(member => ({
          ...member,
          company: safeString(member.company, "CA"),
          location: safeString(member.location, "US"),
          function: safeString(member.function, "Other"),
          focus: safeString(member.focus),
          department: safeString(member.department),
          role: safeString(member.role),
          name: safeString(member.name),
          tags: member.tags || []
        }));

      // Cache the result
      this.cache.set(cacheKey, processedMembers);
      this.cacheTimestamps.set(cacheKey, Date.now());
      
      console.log('✅ RosterDataService: Processed', processedMembers.length, 'roster members');
      return processedMembers;
      
    } catch (error) {
      console.error('❌ RosterDataService: Error fetching roster data:', error);
      
      // Fallback to local data
      const fallbackData = (itRosterData || []).map(member => ({
        ...member,
        company: safeString(member.company, "CA"),
        location: safeString(member.location, "US"),
        function: safeString(member.function, "Other"),
        name: safeString(member.name)
      }));
      
      console.log('📊 RosterDataService: Using fallback data:', fallbackData.length, 'members');
      return fallbackData;
    }
  }

  /**
   * Fetch outcome allocations with caching and optimization
   */
  async fetchOutcomeAllocations(year) {
    const cacheKey = `outcome-allocations-${year}`;
    
    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      console.log('📊 RosterDataService: Using cached outcome allocations for year', year);
      return this.cache.get(cacheKey);
    }

    try {
      console.log('📊 RosterDataService: Fetching outcome allocations for year', year);
      
      let allocationsData;
            try {
        console.log('📊 RosterDataService: Trying roster-specific allocation endpoint...');
        allocationsData = await api.getRosterOutcomeAllocations(year);
        console.log('✅ RosterDataService: Using roster-specific allocations:', allocationsData?.length || 0);
            } catch (rosterError) {
        console.log('📊 RosterDataService: Roster endpoint failed, falling back to general endpoint:', rosterError.message);
        try {
          allocationsData = await api.getOutcomeAllocations(year);
          console.log('✅ RosterDataService: Using general allocations:', allocationsData?.length || 0);
        } catch (generalError) {
          console.log('📊 RosterDataService: General endpoint failed, trying staffing data approach:', generalError.message);
          
          // Final fallback: get outcomes and extract staffing data
          const outcomesResponse = await api.getOutcomes();
          const outcomes = outcomesResponse.outcomes || outcomesResponse || [];
          
          console.log('📊 RosterDataService: Fetching staffing data for', outcomes.length, 'outcomes');
          
          const allocationPromises = outcomes.map(async (outcome) => {
            try {
              const staffingData = await api.getStaffingData(outcome.id);
              
              if (staffingData && staffingData.milestones) {
                // Extract allocations from staffing data
                const outcomeAllocations = [];
                
                staffingData.milestones.forEach(milestone => {
                  (milestone.roles || []).forEach(role => {
                    if (role.roster_member_id) {
                      outcomeAllocations.push({
                        outcome_id: outcome.id,
                        outcome_name: outcome.name,
                        member_id: role.roster_member_id,
                        milestone_id: milestone.id,
                        milestone_name: milestone.name,
                        role_name: role.name,
                        allocation: role.allocation || 0,
                        monthly_allocations: this.calculateMonthlyAllocations(role, milestone),
                        company: role.company || 'CA',
                        location: role.location || 'US',
                        function: role.function || 'Other',
                        rate: role.rate || 0,
                        capex_classification: role.capex_classification || 'OPEX'
                      });
                    }
                  });
                });
                
                return outcomeAllocations;
              }
              
              return [];
            } catch (error) {
              console.warn(`RosterDataService: Failed to get staffing data for outcome ${outcome.id}:`, error.message);
              return [];
            }
          });
          
          const allAllocations = await Promise.all(allocationPromises);
          allocationsData = allAllocations.flat();
          
          console.log('✅ RosterDataService: Extracted allocations from staffing data:', allocationsData.length, 'allocations');
        }
      }

            // Debug the allocation data structure
      console.log('🔍 RosterDataService: Raw allocations data structure:', {
        isArray: Array.isArray(allocationsData),
        keys: allocationsData ? Object.keys(allocationsData) : 'null',
        outcome_allocations_length: allocationsData?.outcome_allocations?.length || 'none',
        direct_length: allocationsData?.length || 'none',
        sample_item: allocationsData?.outcome_allocations?.[0] || allocationsData?.[0] || 'none'
      });

      // Handle the actual API response structure
      // Based on debugging, the API returns allocations as a direct array, not wrapped in outcome_allocations
      let processedAllocations;
      if (Array.isArray(allocationsData)) {
        // Direct array response (this is what we're actually getting)
        processedAllocations = allocationsData;
        console.log('✅ RosterDataService: Using direct array response');
      } else if (allocationsData?.outcome_allocations && Array.isArray(allocationsData.outcome_allocations)) {
        // Wrapped response (fallback)
        processedAllocations = allocationsData.outcome_allocations;
        console.log('✅ RosterDataService: Using wrapped response');
      } else {
        // Empty fallback
        processedAllocations = [];
        console.warn('⚠️ RosterDataService: No valid allocation data found, using empty array');
      }

      // Debug the processed allocations
      if (processedAllocations.length > 0) {
        const sampleAllocation = processedAllocations[0];
        console.log('🔍 RosterDataService: Sample processed allocation:', {
          outcome_id: sampleAllocation.outcome_id,
          outcome_name: sampleAllocation.outcome_name,
          member_id: sampleAllocation.member_id,
          has_staffed_roles: !!sampleAllocation.staffed_roles,
          staffed_roles_length: sampleAllocation.staffed_roles?.length || 0,
          sample_role: sampleAllocation.staffed_roles?.[0] || 'none'
        });
      }

      // Cache the result
      this.cache.set(cacheKey, processedAllocations);
      this.cacheTimestamps.set(cacheKey, Date.now());
      
      console.log('✅ RosterDataService: Processed', processedAllocations.length, 'outcome allocations');
      return processedAllocations;
      
    } catch (error) {
      console.error('❌ RosterDataService: Error fetching outcome allocations:', error);
      
      // Return empty array as fallback
      const fallbackData = [];
      console.log('📊 RosterDataService: Using empty fallback allocation data');
      return fallbackData;
    }
  }

  /**
   * Fetch unassigned roles with optimization (excludes cost estimation)
   */
  async fetchUnassignedRoles(year) {
    const cacheKey = `unassigned-roles-${year}`;
    
    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      console.log('📊 RosterDataService: Using cached unassigned roles for year', year);
      return this.cache.get(cacheKey);
    }

    try {
      console.log('📊 RosterDataService: Fetching unassigned roles for year', year);
      
      let roles = [];
      
      // Try the new optimized roster-specific endpoint first
      try {
        console.log('📊 RosterDataService: Attempting optimized roster unassigned roles endpoint...');
        roles = await api.getRosterUnassignedRoles(year);
        console.log('✅ RosterDataService: Using optimized roster unassigned roles endpoint:', roles.length, 'staffing roles');
      } catch (rosterError) {
        console.log('📊 RosterDataService: Optimized roster unassigned roles endpoint not available, falling back to general endpoint');
        
        try {
          console.log('📊 RosterDataService: Attempting fallback to general unassigned roles endpoint...');
          const allRoles = await api.getUnassignedRoles(year);
          
          // Client-side filtering (should be avoided with proper backend endpoints)
          console.log('⚠️ RosterDataService: Using client-side filtering of', allRoles.length, 'unassigned roles');
          
          // Filter out cost estimation roles
          roles = allRoles.filter(role => 
            !role.milestone_name || !role.milestone_name.toLowerCase().includes('cost estimation')
          );
          
          console.log('✅ RosterDataService: Using filtered data from fallback endpoint:', roles.length, 'staffing roles');
        } catch (fallbackError) {
          console.error('❌ RosterDataService: Both roster and general unassigned roles endpoints failed:', fallbackError.message);
          roles = [];
        }
      }
      
      // Transform unassigned roles to match member structure
      const unassignedMembers = roles.map(role => ({
        id: `unassigned-${role.id}`,
        name: role.name,
        function: role.function || 'Other',
        role: role.role || '',
        company: 'Unassigned',
        location: 'Various',
        isUnassigned: true,
        unassignedRoleData: {
          ...role,
          source: role.source || 'unknown'
        },
        outcome_name: role.outcome_name,
        tags: []
      }));

      // Cache the result
      this.cache.set(cacheKey, unassignedMembers);
      this.cacheTimestamps.set(cacheKey, Date.now());
      
      console.log('✅ RosterDataService: Processed', unassignedMembers.length, 'unassigned roles');
      return unassignedMembers;
      
    } catch (error) {
      console.error('❌ RosterDataService: Critical error fetching unassigned roles:', error);
      return [];
    }
  }

  /**
   * Fetch supporting data (outcomes, applications, tags, rates)
   */
  async fetchSupportingData() {
    const cacheKey = 'supporting-data';
    
    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      console.log('📊 RosterDataService: Using cached supporting data');
      return this.cache.get(cacheKey);
    }

    try {
      console.log('📊 RosterDataService: Loading supporting data...');
      
      const [outcomesResponse, applicationsResponse, tagsResponse, ratesResponse] = await Promise.allSettled([
        api.getOutcomes(),
        api.getApplications(),
        api.getTags(),
        api.getRates()
      ]);
      
      const result = {
        outcomes: [],
        availableOutcomes: [],
        applications: [],
        availableApplications: [],
        availableTags: [],
        companyOptions: ['CA', 'US', 'Other'],
        locationOptions: [
          { code: 'US', display: 'United States' },
          { code: 'CA', display: 'Canada' }
        ]
      };
      
      // Process outcomes
      if (outcomesResponse.status === 'fulfilled') {
        const outcomesData = outcomesResponse.value;
        const outcomesArray = Array.isArray(outcomesData) ? outcomesData : (outcomesData.outcomes || []);
        const activeOutcomes = outcomesArray
          .filter(outcome => !outcome.deleted)
          .map(outcome => ({
            ...outcome,
            name: safeString(outcome.name),
            applications: Array.isArray(outcome.applications) ? outcome.applications : []
          }));
        
        result.outcomes = activeOutcomes;
        result.availableOutcomes = activeOutcomes.map(o => safeString(o.name)).filter(n => n).sort();
        console.log('✅ RosterDataService: Loaded outcomes:', activeOutcomes.length);
      } else {
        console.warn('RosterDataService: Failed to load outcomes:', outcomesResponse.reason);
      }
      
      // Process applications
      if (applicationsResponse.status === 'fulfilled') {
        const applicationsData = applicationsResponse.value.applications || applicationsResponse.value || [];
        if (Array.isArray(applicationsData)) {
          result.applications = applicationsData;
          result.availableApplications = applicationsData.map(app => safeString(app.name)).filter(n => n).sort();
          console.log('✅ RosterDataService: Loaded applications:', applicationsData.length);
        }
      } else {
        console.warn('RosterDataService: Failed to load applications:', applicationsResponse.reason);
      }
      
      // Process tags
      if (tagsResponse.status === 'fulfilled') {
        const tagsData = tagsResponse.value.tags || tagsResponse.value || [];
        if (Array.isArray(tagsData)) {
          result.availableTags = tagsData;
          console.log('✅ RosterDataService: Loaded tags:', tagsData.length);
        }
      } else {
        console.warn('RosterDataService: Failed to load tags:', tagsResponse.reason);
      }
      
      // Process rates for company/location options
      if (ratesResponse.status === 'fulfilled') {
        const rates = ratesResponse.value.rates || [];
        const companies = [...new Set(rates.map(rate => safeString(rate.company)).filter(c => c))].sort();
        if (companies.length > 0) {
          result.companyOptions = companies;
        }
        
        const locationCodes = [...new Set(rates.map(rate => safeString(rate.location)).filter(l => l))].sort();
        const locationOptionsWithDisplay = locationCodes.map(code => ({
          code: code,
          display: getLocationDisplayName(code)
        }));
        if (locationOptionsWithDisplay.length > 0) {
          result.locationOptions = locationOptionsWithDisplay;
        }
        console.log('✅ RosterDataService: Loaded rates for', companies.length, 'companies and', locationCodes.length, 'locations');
      } else {
        console.warn('RosterDataService: Failed to load rates:', ratesResponse.reason);
      }
      
      // Cache the result
      this.cache.set(cacheKey, result);
      this.cacheTimestamps.set(cacheKey, Date.now());
      
      console.log('✅ RosterDataService: Supporting data loaded successfully');
      return result;
      
    } catch (error) {
      console.error('❌ RosterDataService: Error loading supporting data:', error);
      
      // Return minimal fallback data
      return {
        outcomes: [],
        availableOutcomes: [],
        applications: [],
        availableApplications: [],
        availableTags: [],
        companyOptions: ['CA', 'US', 'Other'],
        locationOptions: [
          { code: 'US', display: 'United States' },
          { code: 'CA', display: 'Canada' }
        ]
      };
    }
  }

    

  /**
   * Helper method to calculate monthly allocations from role data
   */
  calculateMonthlyAllocations(role, milestone) {
    const monthlyAllocations = Array(12).fill(0);
    
    if (role.dateRanges && role.dateRanges.length > 0) {
      // Use dateRanges if available
      role.dateRanges.forEach(range => {
        const startMonth = Math.max(1, Math.min(12, range.startMonth || 1));
        const endMonth = Math.max(startMonth, Math.min(12, range.endMonth || startMonth));
        const allocation = Math.max(0, Math.min(100, range.allocation || role.allocation || 0));
        
        for (let month = startMonth; month <= endMonth; month++) {
          const monthIndex = month - 1; // Convert to 0-based index
          if (monthIndex >= 0 && monthIndex < 12) {
            monthlyAllocations[monthIndex] = allocation;
          }
        }
      });
    } else if (milestone) {
      // Fallback to milestone dates
      const startDate = new Date(milestone.start_date);
      const endDate = new Date(milestone.end_date);
      const startMonth = startDate.getMonth() + 1; // 1-based
      const endMonth = endDate.getMonth() + 1; // 1-based
      const allocation = Math.max(0, Math.min(100, role.allocation || 0));
      
      for (let month = startMonth; month <= endMonth; month++) {
        const monthIndex = month - 1; // Convert to 0-based index
        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyAllocations[monthIndex] = allocation;
        }
      }
    }
    
    return monthlyAllocations;
  }

  /**
   * Check if cache entry is valid
   */
  isCacheValid(key) {
    if (!this.cache.has(key) || !this.cacheTimestamps.has(key)) {
      return false;
    }
    
    const timestamp = this.cacheTimestamps.get(key);
    return (Date.now() - timestamp) < this.CACHE_DURATION;
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    console.log('🧹 RosterDataService: Clearing all cached data');
    this.cache.clear();
    this.cacheTimestamps.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      entries: this.cache.size,
      keys: Array.from(this.cache.keys()),
      timestamps: Array.from(this.cacheTimestamps.entries()).map(([key, timestamp]) => ({
        key,
        age: Date.now() - timestamp,
        valid: this.isCacheValid(key)
      }))
    };
  }

  /**
   * Force refresh specific data type
   */
  async forceRefresh(dataType, ...args) {
    // Clear relevant cache entries
    const keysToDelete = Array.from(this.cache.keys()).filter(key => key.includes(dataType));
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.cacheTimestamps.delete(key);
    });
    
    // Fetch fresh data
    switch (dataType) {
      case 'roster':
        return this.fetchRosterData();
      case 'allocations':
        return this.fetchOutcomeAllocations(args[0]);
      case 'unassigned':
        return this.fetchUnassignedRoles(args[0]);
      case 'supporting':
        return this.fetchSupportingData();
      default:
        console.warn('RosterDataService: Unknown data type for force refresh:', dataType);
        return null;
    }
  }
}

export { RosterDataService };